import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as moment from 'moment';
import Tooltip from '@material-ui/core/Tooltip';
import { withStyles } from '@material-ui/styles';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import DashboardAPI from 'apis/dashboard/dashboard';

// TODO: confirm the real route of "My Benefit Statement"
const BENEFIT_STATEMENT_ROUTE = '/dashboard/benefit-statement';

const C = {
  navy: '#0b1245',
  ink: '#1f2937',
  blue: '#0a6ed1',
  border: '#4aa3ee',
  bg: '#eaf4fe',
  tileBorder: '#d6e4f5',
  font: "'DM Sans', sans-serif",
};

const InfoTooltip = withStyles({
  tooltip: {
    color: '#111827',
    backgroundColor: '#ffffff',
    border: '1px solid #d6e4f5',
    boxShadow: '0 4px 14px rgba(15,23,42,0.18)',
    maxWidth: 300,
    fontSize: 13,
    lineHeight: 1.45,
    padding: '10px 14px',
    fontFamily: "'DM Sans', sans-serif",
  },
  arrow: {
    color: '#ffffff',
  },
})(Tooltip);

const periodEnd = moment().format('YYYY-MM-DD');

// Signature has no sample data: its values come only from the API.
// Thrive is unchanged and still uses its static sample values.
const PROGRAMS = {
  signature: {
    title: 'Your Signature value',
    tail: 'in savings and rewards from Signature.',
    tooltip:
      'Estimated savings compared with standard pricing over the last 12 complete months. Not an invoice.',
  },
  thrive: {
    title: 'Your Thrive value',
    tail: 'in rewards from Thrive Rewards.',
    tooltip:
      'Estimated value of the Thrive Rewards you earned over the last 12 complete months. Not an invoice.',
    sample: {
      total: 612,
      periodEnd,
      breakdown: [
        { label: 'Points earned', amount: 612 },
        { label: 'Free shipping', amount: 'Only For signature' },
        { label: 'Supplies', amount: 'Only For signature' },
      ],
    },
  },
};

const money = (n) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(n || 0));

// small count-up so the big number rolls up once when it appears
function useAnimatedNumber(target, active, duration = 900) {
  const [value, setValue] = useState(0);
  const frameRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;

    const start = performance.now();
    const end = Number(target) || 0;

    const tick = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(end * eased));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      }
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [target, active, duration]);

  return value;
}

/*
  Props
  - program: 'signature' | 'thrive'
  - customerAccountNumber: the shipto
  - loyaltyId: the loyalty id (lId)
*/
export default function BenefitValueCard({
  program = 'signature',
  customerAccountNumber,
  loyaltyId,
  showViewStatementLink = true,
  showPeriod = true,
  onDataLoaded,
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const { getViewMyBenefitDetails } = DashboardAPI();

  const config = PROGRAMS[program] || PROGRAMS.signature;

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);

  // keep the latest callback without re-running the fetch effect
  const onLoadedRef = useRef(onDataLoaded);
  onLoadedRef.current = onDataLoaded;
  const notify = (payload) => {
    if (typeof onLoadedRef.current === 'function') onLoadedRef.current(payload);
  };

  useEffect(() => {
    let cancelled = false;

    // Thrive: unchanged, static values, no API call
    if (program !== 'signature') {
      const sample = config.sample || null;
      setInfo(sample);
      setLoading(false);
      if (sample) {
        notify({
          pointsEarning: sample.breakdown[0].amount,
          total: sample.total,
          date: moment().format('MM/DD/YYYY'),
        });
      }
      return undefined;
    }

    // Signature: needs shipto and loyalty id before calling the API
    if (!customerAccountNumber || !loyaltyId) {
      setInfo(null);
      setLoading(true);
      return undefined;
    }

    (async () => {
      setLoading(true);
      let result = null;
      let payload = null; // null = request error

      try {
        // GET /dashboard/view-my-benefit-details?shipto={shipto}&lId={loyaltyId}
        const res = await getViewMyBenefitDetails(customerAccountNumber, loyaltyId);

        // Not eligible (less than / exactly 1 year in Signature) -> show nothing
        const notEligible =
          res?.eligible === false ||
          res?.data?.eligible === false ||
          res?.status === 403;

        // Backend returns { message, data }. Handle both wrapped and unwrapped responses.
        const d = notEligible ? null : (res?.data?.data ?? res?.data ?? null);

        if (notEligible) {
          payload = {
            eligible: false,
            availableFrom: res?.availableFrom ?? res?.data?.availableFrom ?? null,
          };
        } else if (d && typeof d === 'object' && d.eligible !== false) {
          payload = d;

          const breakdown = [
            { label: 'Points earned', amount: Number(d.pointsEarning || 0) },
            { label: 'Free shipping', amount: Number(d.shippingSavings || 0) },
            { label: 'Supplies', amount: Number(d.suppliesSavings || 0) },
          ];

          const fallbackTotal = breakdown.reduce((s, b) => s + b.amount, 0);
          const apiTotal = Number(d.total);

          result = {
            total: Number.isFinite(apiTotal) ? apiTotal : fallbackTotal,
            periodEnd: d.date
              ? moment(d.date, 'MM/DD/YYYY').format('YYYY-MM-DD')
              : periodEnd,
            breakdown,
          };
        }
      } catch (e) {
        // network error -> card stays hidden, parent gets null
        console.log({ e });
      }

      if (cancelled) return;

      setInfo(result);
      setLoading(false);
      notify(payload);
    })();

    return () => {
      cancelled = true;
    };
  }, [customerAccountNumber, loyaltyId, program]);

  const animatedTotal = useAnimatedNumber(info?.total, !loading && !!info);

  // while loading, not eligible, or no data -> render nothing (no empty box)
  if (loading || !info) return null;

  return (
    <div
      style={{
        marginTop: 20,
        background: C.bg,
        border: `1.5px solid ${C.border}`,
        borderRadius: 10,
        padding: '14px 20px 12px',
        textAlign: 'left',
        fontFamily: C.font,
        color: C.ink,
        boxSizing: 'border-box',
      }}
    >
      {/* ---------- header ---------- */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          gap: 6,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
          <span style={{ fontSize: 20, fontWeight: 700, color: C.navy, letterSpacing: '-0.01em' }}>
            {t(config.title)}
          </span>
          <span style={{ fontSize: 16, fontWeight: 400, color: C.ink }}>
            · {t('last 12 months')}
          </span>
          <InfoTooltip title={t(config.tooltip)} placement="top" arrow>
            <InfoOutlinedIcon
              tabIndex={0}
              aria-label={t(config.tooltip)}
              style={{ fontSize: 18, color: C.blue, cursor: 'pointer' }}
            />
          </InfoTooltip>
        </div>

        {showPeriod && info.periodEnd && (
          <div style={{ fontSize: 15, color: C.ink }}>
            {t('Through')} {moment(info.periodEnd).format('MMM DD, YYYY')}
          </div>
        )}
      </div>

      {/* ---------- big number + message ---------- */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          columnGap: 20,
          rowGap: 2,
          margin: '4px 0 10px',
        }}
      >
        <div
          style={{
            fontSize: 'clamp(36px, 5vw, 56px)',
            fontWeight: 700,
            color: C.navy,
            lineHeight: 1.05,
            letterSpacing: '-0.03em',
          }}
        >
          {money(animatedTotal)}
        </div>

        <div style={{ fontSize: 16, lineHeight: 1.35 }}>
          <div>{t(config.tail)}</div>
        </div>
      </div>

      {/* ---------- breakdown tiles ---------- */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 12,
        }}
      >
        {info.breakdown.map((item) => (
          <div
            key={item.label}
            style={{
              background: '#ffffff',
              border: `1px solid ${C.tileBorder}`,
              borderRadius: 6,
              padding: '9px 16px 10px',
            }}
          >
            <div style={{ fontSize: 14, color: C.ink }}>{t(item.label)}</div>
            <div
              style={{
                fontSize: typeof item.amount === 'string' ? 14 : 22,
                fontWeight: 700,
                color: typeof item.amount === 'string' ? '#6b7280' : C.navy,
                marginTop: 2,
                letterSpacing: '-0.02em',
              }}
            >
              {typeof item.amount === 'string' ? item.amount : money(item.amount)}
            </div>
          </div>
        ))}
      </div>

      {/* ---------- link (dashboard only) ---------- */}
      {showViewStatementLink && (
        <button
          type="button"
          onClick={() => history.push(BENEFIT_STATEMENT_ROUTE)}
          style={{
            marginTop: 10,
            padding: 0,
            border: 'none',
            background: 'transparent',
            color: C.blue,
            fontSize: 15,
            fontFamily: C.font,
            textDecoration: 'underline',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {t('View full benefit statement')}
          <ArrowForwardIcon style={{ fontSize: 16 }} />
        </button>
      )}
    </div>
  );
}